import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useDeclineInvitationMutation } from "Auth/Api/Mutations/Invitations/useDeclineInvitationMutation";
import { useAcceptInvitationMutation } from "Auth/Api/Mutations/Invitations/useAcceptInvitationMutation";
import { useSwitchContextMutation } from "Auth/Api/Mutations/Context/useSwitchContextMutation";
import { useGetMyInvitationsQuery } from "Auth/Api/Queries/Invitations/useGetMyInvitationsQuery";
import { InvitationCard } from "Components/Invitations/InvitationCard";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import type {
  AcceptInvitationResult,
  SignInResult,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { setToken } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { appUrl } from "Utils/UrlUtils";
import { useState } from "react";

const COMPONENT_RESOURCES = Resources.Auth.Invitations;

/**
 * Inbox of pending RoleInvitations for the current Identity. Accept flips the Pending IdentityUser
 * to Active server-side (Merchant: to Onboarding — AuthenticatedRoute then routes it into the add-role
 * wizard, which ends with the bank connection), then immediately switches the JWT context to it
 * (hard-navigate, matching ContextSwitcher) so the user lands in the new role with the right permissions.
 *
 * Decline marks the invitation as Declined; the inbox refreshes itself via React Query invalidation.
 */
export const InvitationsInboxPageContent = () => {
  const { t } = useResource();
  const [acceptingPublicID, setAcceptingPublicID] = useState<string | null>(
    null,
  );
  const [decliningPublicID, setDecliningPublicID] = useState<string | null>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, error } = useGetMyInvitationsQuery();

  // Two chained mutations: accept produces the new IdentityUser PublicID, then we switch context
  // to it. Use the same hard-navigate pattern as ContextSwitcher so the new JWT atomically replaces
  // the old one in localStorage on app bootstrap.
  const { mutate: switchContext } = useSwitchContextMutation({
    onSuccess: (result: SignInResult) => {
      setToken(result);
      window.location.replace(appUrl("homepage"));
    },
    onError: () => {
      setAcceptingPublicID(null);
      setActionError(t(COMPONENT_RESOURCES.AcceptedButSwitchFailed));
    },
  });

  const { mutate: acceptInvitation } = useAcceptInvitationMutation({
    onSuccess: (result: AcceptInvitationResult) => {
      switchContext({ identityUserPublicID: result.identityUserPublicID });
    },
    onError: () => {
      setAcceptingPublicID(null);
      setActionError(t(COMPONENT_RESOURCES.AcceptError));
    },
  });

  const { mutate: declineInvitation } = useDeclineInvitationMutation({
    onSuccess: () => {
      setDecliningPublicID(null);
    },
    onError: () => {
      setDecliningPublicID(null);
      setActionError(t(COMPONENT_RESOURCES.DeclineError));
    },
  });

  const handleAccept = (publicID: string) => {
    setActionError(null);
    setAcceptingPublicID(publicID);
    acceptInvitation(publicID);
  };

  const handleDecline = (publicID: string) => {
    setActionError(null);
    setDecliningPublicID(publicID);
    declineInvitation(publicID);
  };

  return (
    <PrimaryPaper>
      <Stack gap={3}>
        <Typography variant="body1" color="text.secondary">
          {t(COMPONENT_RESOURCES.PageDescription)}
        </Typography>

        {actionError && <Alert severity="error">{actionError}</Alert>}

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">{t(COMPONENT_RESOURCES.LoadError)}</Alert>
        )}

        {!isLoading && !error && data && data.items.length === 0 && (
          <Typography variant="body2" color="text.secondary" py={4}>
            {t(COMPONENT_RESOURCES.EmptyState)}
          </Typography>
        )}

        {!isLoading && data && data.items.length > 0 && (
          <Stack gap={2}>
            {data.items.map((invitation) => (
              <InvitationCard
                key={invitation.publicID}
                invitation={invitation}
                isAccepting={acceptingPublicID === invitation.publicID}
                isDeclining={decliningPublicID === invitation.publicID}
                onAccept={handleAccept}
                onDecline={handleDecline}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </PrimaryPaper>
  );
};
